import React, { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { LinkIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import CloudEvalAnalysis from './CloudEvalAnalysis';
import CloudEvalBreadcrumbs from './CloudEvalBreadcrumbs';
import { resolveCaseNumber } from './cloudEvalAdapters';
import { cloudEvalPath } from './cloudEvalRoutes';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatEvalLabel(evaluation) {
  if (!evaluation) return '';
  const caseNumber = resolveCaseNumber(evaluation);
  const startedAt = evaluation.eval_started ? new Date(evaluation.eval_started).toLocaleString() : 'Unknown start';
  return caseNumber ? `${caseNumber} - ${startedAt}` : startedAt;
}

function routeEvalLabel(evaluation) {
  return resolveCaseNumber(evaluation) || evaluation?.id || '';
}

function LoadingSpinner({ label = 'Loading Cloud Evals' }) {
  return (
    <div className="flex min-h-[240px] items-center justify-center" role="status">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

function ErrorNotice({ message }) {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <h3 className="text-sm font-medium text-red-800">Error</h3>
      <p className="mt-2 text-sm text-red-700">{message}</p>
    </div>
  );
}

function RouteNotFound({ title, backTo, backLabel }) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-6">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 text-sm text-gray-600">This Cloud Evals link is invalid or the record is no longer available.</p>
      <Link className="mt-4 inline-flex text-sm font-medium text-indigo-600 hover:text-indigo-800" to={backTo}>
        {backLabel}
      </Link>
    </div>
  );
}

function EvalListbox({ label, value, options, onChange, placeholder, getOptionLabel, loading = false }) {
  return (
    <div className="max-w-md">
      <Listbox value={value} onChange={onChange} disabled={loading}>
        {({ open }) => (
          <>
            <Listbox.Label className="block text-sm font-medium leading-6 text-gray-900">
              {label}
            </Listbox.Label>
            <div className="relative mt-2">
              <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm sm:leading-6">
                <span className="block truncate">
                  {loading ? `Loading ${label.toLowerCase()}s…` : value ? getOptionLabel(value) : placeholder}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>

              <Transition
                as={React.Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                show={open}
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {options.map((option) => (
                    <Listbox.Option
                      className={({ active }) => classNames(
                        active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                        'relative cursor-default select-none py-2 pl-3 pr-9'
                      )}
                      key={option.id}
                      value={option}
                    >
                      {({ selected, active }) => (
                        <>
                          <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                            {getOptionLabel(option)}
                          </span>
                          {selected && (
                            <span className={classNames(
                              active ? 'text-white' : 'text-indigo-600',
                              'absolute inset-y-0 right-0 flex items-center pr-4'
                            )}>
                              <CheckIcon aria-hidden="true" className="h-5 w-5" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  );
}

export default function ViewWebEval() {
  const { organizationId, clientId, evaluationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [organizations, setOrganizations] = useState([]);
  const [clients, setClients] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [loadingClients, setLoadingClients] = useState(Boolean(organizationId));
  const [loadingEvaluations, setLoadingEvaluations] = useState(Boolean(clientId));
  const [error, setError] = useState(null);
  const [masterResults, setMasterResults] = useState(null);
  const [copyStatus, setCopyStatus] = useState('idle');

  const selectedOrg = useMemo(
    () => organizations.find((organization) => organization.id === organizationId) ?? null,
    [organizations, organizationId]
  );
  const selectedClient = useMemo(
    () => clients.find((client) => client.id === clientId) ?? null,
    [clients, clientId]
  );
  const selectedEval = useMemo(
    () => evaluations.find((evaluation) => evaluation.id === evaluationId) ?? null,
    [evaluations, evaluationId]
  );

  useEffect(() => {
    let active = true;

    async function fetchOrganizations() {
      setLoadingOrganizations(true);
      setError(null);
      try {
        const db = getFirestore();
        const orgsSnapshot = await getDocs(collection(db, 'organizations'));
        if (active) {
          setOrganizations(orgsSnapshot.docs.map((organizationDoc) => ({
            id: organizationDoc.id,
            ...organizationDoc.data(),
          })));
        }
      } catch (err) {
        if (active) setError('Failed to load organizations');
        console.error('Error fetching organizations:', err);
      } finally {
        if (active) setLoadingOrganizations(false);
      }
    }

    fetchOrganizations();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setClients([]);

    if (!organizationId || loadingOrganizations || !selectedOrg) {
      setLoadingClients(Boolean(organizationId && loadingOrganizations));
      return () => {
        active = false;
      };
    }

    async function fetchClients() {
      setLoadingClients(true);
      setError(null);
      try {
        const db = getFirestore();
        const clientsQuery = query(
          collection(db, 'clients'),
          where('organization_id', '==', selectedOrg.id)
        );
        const clientsSnapshot = await getDocs(clientsQuery);
        if (active) {
          setClients(clientsSnapshot.docs.map((clientDoc) => ({
            id: clientDoc.id,
            ...clientDoc.data(),
          })));
        }
      } catch (err) {
        if (active) setError('Failed to load clients');
        console.error('Error fetching clients:', err);
      } finally {
        if (active) setLoadingClients(false);
      }
    }

    fetchClients();
    return () => {
      active = false;
    };
  }, [organizationId, loadingOrganizations, selectedOrg]);

  useEffect(() => {
    let active = true;
    setEvaluations([]);
    setMasterResults(null);

    if (!clientId || loadingClients || !selectedClient) {
      setLoadingEvaluations(Boolean(clientId && loadingClients));
      return () => {
        active = false;
      };
    }

    async function fetchEvaluations() {
      setLoadingEvaluations(true);
      setError(null);
      try {
        const db = getFirestore();
        const evalsQuery = query(
          collection(db, 'cloud_eval_results'),
          where('client', '==', selectedClient.id)
        );
        const evalsSnapshot = await getDocs(evalsQuery);
        if (active) {
          setEvaluations(evalsSnapshot.docs.map((evaluationDoc) => ({
            id: evaluationDoc.id,
            ...evaluationDoc.data(),
          })));
        }
      } catch (err) {
        if (active) setError('Failed to load evaluations');
        console.error('Error fetching evaluations:', err);
      } finally {
        if (active) setLoadingEvaluations(false);
      }
    }

    async function fetchMaster() {
      try {
        const db = getFirestore();
        const sex = selectedClient.Sex === 0 ? 'male' : 'female';
        const masterDoc = await getDoc(doc(db, 'master_results', sex));
        if (active) setMasterResults(masterDoc.exists() ? masterDoc.data() : null);
      } catch (err) {
        console.error('Error fetching master results:', err);
        if (active) setMasterResults(null);
      }
    }

    fetchEvaluations();
    fetchMaster();
    return () => {
      active = false;
    };
  }, [clientId, loadingClients, selectedClient]);

  useEffect(() => {
    setCopyStatus('idle');
  }, [location.pathname]);

  async function copyEvalLink() {
    const canonicalUrl = `${window.location.origin}${cloudEvalPath({ organizationId, clientId, evaluationId })}`;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(canonicalUrl);
      setCopyStatus('copied');
    } catch (err) {
      console.error('Failed to copy eval link:', err);
      setCopyStatus('failed');
    }
  }

  if (loadingOrganizations) return <LoadingSpinner />;
  if (error) return <ErrorNotice message={error} />;

  if (organizationId && !selectedOrg) {
    return <RouteNotFound backLabel="Back to Cloud Evals" backTo={cloudEvalPath()} title="Organization not found" />;
  }

  if (clientId && !loadingClients && !selectedClient) {
    return (
      <RouteNotFound
        backLabel={`Back to ${selectedOrg.name || 'organization'}`}
        backTo={cloudEvalPath({ organizationId })}
        title="Client not found"
      />
    );
  }

  if (evaluationId && !loadingEvaluations && !selectedEval) {
    return (
      <RouteNotFound
        backLabel={`Back to ${selectedClient.Name || 'client'}`}
        backTo={cloudEvalPath({ organizationId, clientId })}
        title="Evaluation not found"
      />
    );
  }

  return (
    <div className="space-y-6">
      <CloudEvalBreadcrumbs
        client={selectedClient}
        evaluation={selectedEval}
        evaluationLabel={routeEvalLabel(selectedEval)}
        organization={selectedOrg}
      />

      <div className="sm:flex sm:items-start sm:justify-between sm:gap-6">
        <div>
          <h1 className="text-base font-semibold leading-7 text-gray-900">Cloud Evals</h1>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            Select an organization, client, and evaluation to view analysis.
          </p>
        </div>

        {selectedEval && (
          <div className="mt-3 flex items-center gap-3 sm:mt-0">
            <button
              aria-label="Copy eval link"
              className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
              onClick={copyEvalLink}
              type="button"
            >
              <LinkIcon aria-hidden="true" className="h-4 w-4" />
              Copy link
            </button>
            {copyStatus === 'copied' && <span className="text-sm font-medium text-green-700" role="status">Link copied</span>}
            {copyStatus === 'failed' && <span className="text-sm font-medium text-red-700" role="status">Copy failed</span>}
          </div>
        )}
      </div>

      <div className="space-y-6">
        <EvalListbox
          getOptionLabel={(organization) => organization.name}
          label="Organization"
          onChange={(organization) => navigate(cloudEvalPath({ organizationId: organization.id }))}
          options={organizations}
          placeholder="Select an organization to continue"
          value={selectedOrg}
        />

        {selectedOrg && (
          <EvalListbox
            getOptionLabel={(client) => client.Name}
            label="Client"
            loading={loadingClients}
            onChange={(client) => navigate(cloudEvalPath({ organizationId: selectedOrg.id, clientId: client.id }))}
            options={clients}
            placeholder="Select a client to continue"
            value={selectedClient}
          />
        )}

        {selectedClient && (
          <EvalListbox
            getOptionLabel={formatEvalLabel}
            label="Evaluation"
            loading={loadingEvaluations}
            onChange={(evaluation) => navigate(cloudEvalPath({
              organizationId: selectedOrg.id,
              clientId: selectedClient.id,
              evaluationId: evaluation.id,
            }))}
            options={evaluations}
            placeholder="Select an evaluation to continue"
            value={selectedEval}
          />
        )}

        {selectedEval && (
          <CloudEvalAnalysis client={selectedClient} evaluation={selectedEval} masterResults={masterResults} />
        )}
      </div>
    </div>
  );
}
